import React, { useEffect,  useState } from 'react';
import { TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import Button from '../../../../components/Button';
import WorkbookV2Picker from '../../../../components/WorkbookV2Picker';
import ToolItemForm from './ToolItemForm';
import ToolItemsList from './ToolItemsList';

const initItemState = {
  type: 'content',
  title: '',
  question: '',
  placeholder: '',
  options: [],
  priority: 1,
  points: 1,
  subtype: '',
  minRating: 0,
  maxRating: 10
};

const types = ['content', 'radio', 'input', 'textarea', 'checkbox', 'rating'];
const toolTypes =  {weekly_tools:"Weekly Tools",assessments: "Assessments"}

const ToolForm = ({
  title,
  requiredScore,
  type,
  description,
  items,
  relatedWorkbooks,
  handleChange,
  handleItems,
  handleEditItem,
  handleDeleteItem,
  handleDragEnd
}) => {
  const [showItemForm, setShowItemForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [item, setItem] = useState(initItemState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancelForm = () => {
    setItem(initItemState);
    setShowItemForm(false);
    setIsEditing(false);
  };

  useEffect(() => {
    if (showItemForm && !isEditing) {
      setItem((prev) => ({
        ...prev,
        priority: items.length + 1,
        type: types[0]
      }));
    }
  }, [items.length, showItemForm, isEditing]);

  const validateItem = () => {
    const contentToCheck = item.type === 'content' ? item.title : item.question;
    const strippedContent = contentToCheck?.trim();
    return !!strippedContent;
  };

  const handleAddItem = () => {
    if (!validateItem()) {
      return;
    }
    // Prepare item data for API - ensure title is set
    const itemToSend = {
      ...item,
      // For non-content types, copy question to title if title is empty
      title: item.type === 'content' ? item.title : (item.title || item.question)
    };

    setIsSubmitting(true);
    handleItems(itemToSend, () => {
      setItem(initItemState);
      setIsSubmitting(false);
      setShowItemForm(false);
    });
  };

  const handleSubmitEditItem = () => {
    if (!validateItem()) {
      return;
    }

    const itemToSend = {
      ...item,
      title: item.type === 'content' ? item.title : (item.title || item.question)
    };


    setIsSubmitting(true);
    handleEditItem(itemToSend, () => {
      setItem(initItemState);
      setIsSubmitting(false);
      setIsEditing(false);
      setShowItemForm(false);
    });
  };

  const handleEditClick = (existingItem) => {
    // When editing, populate question from title if question doesn't exist
    const itemToEdit = {
      ...existingItem,
      question: existingItem.question || (existingItem.type !== 'content' ? existingItem.title : '')
    };
    setItem(itemToEdit);
    setIsEditing(true);
    setShowItemForm(true);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Tool Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextField
          label="Title"
          name="title"
          value={title || ''}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          label="Required Score"
          name="requiredScore"
          type="number"
          value={requiredScore || 0}
          onChange={handleChange}
          fullWidth
        />
      </div>

      <FormControl fullWidth>
        <InputLabel>Tool Type</InputLabel>
        <Select
          name="type"
          value={type || 'weekly_tools'}
          onChange={handleChange}
          label="Tool Type"
        >
          {Object.entries(toolTypes).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Related Workbooks Picker */}
      <div className="w-full">
        <WorkbookV2Picker
          value={relatedWorkbooks || []}
          onChange={(workbookIds) => {
            handleChange({
              target: {
                name: 'relatedWorkbooks',
                value: workbookIds
              }
            });
          }}
          label="Related Workbooks"
        />
      </div>

      {/* Description */}
      <TextField
        label="Description"
        name="description"
        value={description || ''}
        onChange={handleChange}
        fullWidth
        multiline
        rows={4}
        placeholder="Enter tool description..."
      />

      {/* Items Section */}
      <div className="flex flex-col gap-4 mt-8">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold">Tool Items ({items?.length || 0})</h3>
          {!showItemForm && (
            <Button onClick={() => setShowItemForm(true)}>Add Item</Button>
          )}
        </div>

        {/* Add/Edit Item Form - Inline */}
        {showItemForm && (
          <ToolItemForm
            item={item}
            setItem={setItem}
            isEditing={isEditing}
            isSubmitting={isSubmitting}
            validateItem={validateItem}
            handleSubmitEditItem={handleSubmitEditItem}
            handleAddItem={handleAddItem}
            handleCancelForm={handleCancelForm}
          />
        )}

        {/* Drag and Drop Items List */}
        <ToolItemsList
          items={items}
          handleDragEnd={handleDragEnd}
          handleEditClick={handleEditClick}
          handleDeleteItem={handleDeleteItem}
        />
      </div>
    </div>
  );
};

export default ToolForm;

